/** Error returned (never thrown) by every client call, mirroring the server's `{ code, message, details, hint }`. */
export class QubBaseError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details: string | null = null,
    readonly hint: string | null = null,
  ) {
    super(message);
    this.name = 'QubBaseError';
  }
}

export type Fetch = typeof fetch;

export interface Result<T> {
  data: T | null;
  error: QubBaseError | null;
}

export async function toError(res: Response): Promise<QubBaseError> {
  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    /* not JSON */
  }
  const nested = (body.error && typeof body.error === 'object' ? body.error : null) as Record<string, unknown> | null;
  const message = String(body.message ?? body.msg ?? nested?.message ?? (res.statusText || `Request failed with status ${res.status}`));
  return new QubBaseError(
    message,
    res.status,
    String(body.code ?? body.error_code ?? nested?.code ?? res.status),
    (body.details as string | null) ?? null,
    (body.hint as string | null) ?? null,
  );
}

export function networkError(err: unknown): QubBaseError {
  return new QubBaseError(err instanceof Error ? err.message : 'Network request failed', 0, 'NETWORK_ERROR');
}

/** Headers every request carries: the anon/service key plus the current bearer (user token when signed in). */
export type HeaderSource = () => Promise<Record<string, string>>;

export async function requestJson<T>(fetcher: Fetch, url: string, init: RequestInit, headers: HeaderSource): Promise<Result<T>> {
  try {
    const merged: Record<string, string> = { ...(await headers()), ...(init.headers as Record<string, string> | undefined) };
    // A JSON content type on a bodyless request is rejected by the server, so only send it with a body.
    if (init.body !== undefined && init.body !== null) merged['Content-Type'] ??= 'application/json';
    else delete merged['Content-Type'];
    const res = await fetcher(url, { ...init, headers: merged });
    if (!res.ok) return { data: null, error: await toError(res) };
    if (res.status === 204) return { data: null, error: null };
    const text = await res.text();
    return { data: (text ? JSON.parse(text) : null) as T, error: null };
  } catch (err) {
    return { data: null, error: networkError(err) };
  }
}
